<!-- Picker Component - Unity style dropdown -->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Icon from './Icon.svelte';
  import { onMount } from 'svelte';

  interface PickerOption {
    id: string;
    name: string;
    icon?: string;
    disabled?: boolean;
  }

  export let options: PickerOption[] = [];
  export let placeholder: string = 'Select...';
  export let selectedId: string | null = null;
  export let disabled: boolean = false;
  export let size: 'xs' | 'sm' | 'md' | 'lg' = 'md';
  export let className: string = '';
  export let title: string = '';

  let isOpen = false;
  let pickerElement: HTMLDivElement;

  const dispatch = createEventDispatcher<{
    select: { option: PickerOption };
    change: { selectedId: string | null };
  }>();

  $: selectedOption = options.find(opt => opt.id === selectedId);
  $: displayText = selectedOption ? selectedOption.name : placeholder;

  $: sizeClasses = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  }[size];

  function toggleDropdown() {
    if (!disabled) {
      isOpen = !isOpen;
    }
  }

  function selectOption(option: PickerOption) {
    if (option.disabled) return;

    selectedId = option.id;
    isOpen = false;

    dispatch('select', { option });
    dispatch('change', { selectedId: option.id });
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      isOpen = false;
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown();
    }
  }

  function handleClickOutside(event: MouseEvent) {
    if (pickerElement && !pickerElement.contains(event.target as Node)) {
      isOpen = false;
    }
  }

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });
</script>

<div bind:this={pickerElement} class="picker relative {className}" {title}>
  <!-- Picker Button -->
  <button
    type="button"
    class="picker-button w-full flex items-center justify-between gap-2 {sizeClasses} bg-gray-700 border border-gray-600 rounded text-white transition-all duration-200
           {disabled
      ? 'opacity-50 cursor-not-allowed'
      : 'hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-neon-purple/50 focus:border-neon-purple'}
           {isOpen ? 'ring-2 ring-neon-purple/50 border-neon-purple' : ''}"
    {disabled}
    on:click={toggleDropdown}
    on:keydown={handleKeydown}
    role="combobox"
    aria-expanded={isOpen}
    aria-haspopup="listbox"
    aria-controls="picker-dropdown"
  >
    <div class="flex items-center gap-2 min-w-0 flex-1">
      {#if selectedOption?.icon}
        <Icon name={selectedOption.icon} size="sm" />
      {/if}
      <span class="truncate">{displayText}</span>
    </div>

    <Icon
      name="chevron-down"
      size="xs"
      class="transition-transform duration-200 {isOpen ? 'rotate-180' : ''}"
    />
  </button>

  <!-- Dropdown -->
  {#if isOpen}
    <div
      id="picker-dropdown"
      class="picker-dropdown absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg z-50 max-h-60 overflow-y-auto"
      role="listbox"
    >
      {#each options as option (option.id)}
        <button
          type="button"
          class="picker-option w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors duration-150
                 {option.disabled
            ? 'opacity-50 cursor-not-allowed text-gray-500'
            : 'hover:bg-gray-700 text-white'}
                 {selectedId === option.id ? 'bg-neon-purple/20 text-neon-purple' : ''}"
          disabled={option.disabled}
          on:click={() => selectOption(option)}
          role="option"
          aria-selected={selectedId === option.id}
        >
          {#if option.icon}
            <Icon name={option.icon} size="sm" />
          {/if}
          <span class="truncate">{option.name}</span>
        </button>
      {/each}

      {#if options.length === 0}
        <div class="px-3 py-2 text-sm text-gray-500 text-center">No options available</div>
      {/if}
    </div>
  {/if}
</div>

<style lang="postcss">
  @reference "@/styles/theme.css";
</style>
